<div class="cs-chat" [class.cs-chat--wide]="wide">
	@if (glossaryMissing) {
		<div class="cs-chat__warning" role="status">
			<span class="cs-chat__warning-icon" aria-hidden="true">⚠</span>
			<span>Glossary not configured — term tooltips and “Terms in this answer” are disabled.
				The host app must provide <code>CS_CHAT_GLOSSARY</code>.</span>
		</div>
	}
	<header class="cs-chat__header">
		@if (editingTitle) {
			<input #titleInput
				   class="cs-chat__title-input"
				   [(ngModel)]="titleDraft"
				   (keydown)="onTitleKeydown($event)"
				   (blur)="commitTitle()"
				   aria-label="Chat title"
				   maxlength="80" />
		} @else {
			<button type="button"
					class="cs-chat__title"
					[class.cs-chat__title--editable]="titleEditable"
					[disabled]="!titleEditable"
					(click)="startEditTitle()"
					[attr.title]="titleEditable ? 'Click to rename' : null">{{ title }}</button>
		}
		<button class="cs-chat__width-toggle"
				type="button"
				[class.cs-chat__width-toggle--active]="wide"
				(click)="wide = !wide"
				[attr.aria-pressed]="wide"
				[attr.aria-label]="wide ? 'Narrow chat' : 'Widen chat'"
				[title]="wide ? 'Narrow chat' : 'Widen chat'">↔</button>
	</header>

	<div class="cs-chat__transcript-wrap">
		<div class="cs-chat__transcript" csStickToBottom (csStickPinnedChange)="pinned = $event">
			@for (message of messages; track message.id) {
				<cs-chat-message [message]="message" [showDiagnostics]="debugEnabled" (explain)="onExplain($event)"></cs-chat-message>
			}

			@if (awaitingResponse && showThinking) {
				<div class="cs-chat__working" aria-live="polite">
					<span class="cs-chat__working-dots"><span></span><span></span><span></span></span>
					<span class="cs-chat__working-label">Thinking…</span>
					<span class="cs-chat__working-elapsed" csElapsedTime></span>
				</div>
			}
		</div>

		@if (!pinned) {
			<button class="cs-chat__jump-bottom" type="button" (click)="scrollToBottom()" aria-label="Scroll to bottom" title="Scroll to bottom">
				↓
			</button>
		}
	</div>

	@if (activePlan; as plan) {
		<!-- Sticky progress strip: keeps the running multi-step plan visible above the composer
		     while the report streams (the full checklist still lives inline in the answer). -->
		<div class="cs-chat__plan-strip" aria-live="polite">
			<span class="cs-chat__plan-strip-label">Plan</span>
			<span class="cs-chat__plan-strip-count">{{ plan.doneCount }}/{{ plan.items.length }}</span>
			<div class="cs-chat__plan-strip-bar">
				<div class="cs-chat__plan-strip-fill"
					 [style.width.%]="plan.items.length ? (plan.doneCount / plan.items.length) * 100 : 0"></div>
			</div>
			@if (plan.current) {
				<span class="cs-chat__plan-strip-step" [title]="plan.current">{{ plan.current }}</span>
			}
		</div>
	}

	<form class="cs-chat__composer" (submit)="$event.preventDefault(); submit()">
		<textarea #composer
				  class="cs-chat__input"
				  name="draft"
				  rows="1"
				  placeholder="Type a message…"
				  [(ngModel)]="draft"
				  [disabled]="busy"
				  (keydown)="onKeydown($event)"></textarea>

		<!-- Context-window meter: an icon showing used %, click to open the breakdown popup. -->
		@if (contextUsage) {
			<div class="cs-ctx">
				<button type="button" class="cs-ctx__btn" (click)="ctxOpen = !ctxOpen"
						[class.is-open]="ctxOpen"
						[attr.aria-expanded]="ctxOpen"
						title="Context window used">
					<span class="cs-ctx__ring" [style.--pct]="ctxUsedPct"></span>
					<span class="cs-ctx__pct">{{ ctxUsedPct }}%</span>
				</button>
				@if (ctxOpen) {
					<div class="cs-ctx__pop" (click)="$event.stopPropagation()">
						<div class="cs-ctx__head">
							<span>Context window</span>
							<span class="cs-ctx__total">{{ fmtTokens(contextUsage.usedTokens) }} / {{ fmtTokens(contextUsage.contextWindow) }}</span>
						</div>
						<div class="cs-ctx__bar">
							@for (s of ctxSegments; track s.key) {
								<span class="cs-ctx__seg cs-ctx__seg--{{ s.key }}" [style.width.%]="s.pct" [title]="s.label"></span>
							}
						</div>
						<ul class="cs-ctx__legend">
							@for (s of ctxSegments; track s.key) {
								<li>
									<span class="cs-ctx__dot cs-ctx__seg--{{ s.key }}"></span>
									<span class="cs-ctx__label">{{ s.label }}</span>
									<span class="cs-ctx__val">{{ fmtTokens(s.tokens) }} · {{ pctLabel(s.pct) }}%</span>
								</li>
							}
						</ul>
						@if (contextUsage.cacheReadTokens > 0) {
							<div class="cs-ctx__note">{{ fmtTokens(contextUsage.cacheReadTokens) }} served from cache · split is approximate</div>
						} @else {
							<div class="cs-ctx__note">split is approximate</div>
						}
						@if (debugEnabled) {
							<button type="button" class="cs-ctx__debug-btn" (click)="openDebug()">
								Advanced debug — view full prompt
							</button>
						}
					</div>
				}
			</div>
		}

		@if (busy) {
			<button class="cs-chat__send cs-chat__send--stop" type="button" (click)="cancel()" aria-label="Stop generating">
				<span class="cs-chat__stop-square" aria-hidden="true"></span> Stop
			</button>
		} @else {
			<button class="cs-chat__send" type="submit" [disabled]="!draft.trim()">Send</button>
		}
	</form>

	@if (debugEnabled && debugOpen) {
		<aside class="cs-chat__debug">
			<header class="cs-chat__debug-head">
				<span>Prompt debug</span>
				<button type="button" class="cs-chat__debug-close" (click)="closeDebug()" aria-label="Close">×</button>
			</header>
			@if (!debugContext) {
				<div class="cs-chat__debug-loading">Loading full prompt…</div>
			} @else {
				<div class="cs-chat__debug-body">
					<div class="cs-chat__debug-meta">
						context {{ fmtTokens(debugContext.config.contextWindowTokens) }} ·
						conv {{ debugContext.config.conversationModel }} ·
						analysis {{ debugContext.config.analysisModel }}
					</div>

					<details class="cs-chat__debug-sec" open>
						<summary>System prompt <span class="cs-chat__debug-tok">{{ fmtTokens(debugContext.systemPromptTokens) }}</span></summary>
						<pre class="cs-chat__debug-pre">{{ debugContext.systemPrompt }}</pre>
					</details>

					<details class="cs-chat__debug-sec">
						<summary>Tools ({{ debugContext.tools.length }}) <span class="cs-chat__debug-tok">{{ fmtTokens(debugContext.toolsTokens) }}</span></summary>
						@for (t of debugContext.tools; track t.name) {
							<div class="cs-chat__debug-tool">
								<button type="button" class="cs-chat__debug-tool-head" (click)="toggleDebugTool(t.name)">
									<span class="cs-chat__debug-tool-name">{{ t.name }}</span>
									<span class="cs-chat__debug-tok">{{ fmtTokens(t.tokens) }}</span>
								</button>
								@if (debugToolOpen.has(t.name)) {
									<div class="cs-chat__debug-tool-body">
										<p class="cs-chat__debug-tool-desc">{{ t.description }}</p>
										@if (t.parameters) { <pre class="cs-chat__debug-pre">{{ t.parameters }}</pre> }
									</div>
								}
							</div>
						}
					</details>

					<details class="cs-chat__debug-sec">
						<summary>Messages ({{ messages.length }})</summary>
						@for (m of messages; track $index) {
							<div class="cs-chat__debug-msg cs-chat__debug-msg--{{ m.role }}">
								<span class="cs-chat__debug-msg-role">{{ m.role }}</span>
								<pre class="cs-chat__debug-pre">{{ m.content }}</pre>
							</div>
						}
					</details>
				</div>
			}
		</aside>
	}
</div>
